import type { FC, ReactNode } from 'react'

import { ArrowDown } from 'lucide-react'
import { useDictionary } from '@/lib/i18n'
import Reveal from '@/components/landing/reveal'
import SectionHeading from '@/components/landing/section-heading'

const StorySection: FC = (): ReactNode => {
    const t = useDictionary()
    const { story } = t

    return (
        <section id='story' aria-labelledby='story-title' className='relative py-24 sm:py-32'>
            <div className='mx-auto grid max-w-7xl gap-14 px-5 sm:px-8 lg:grid-cols-[1.35fr_1fr] lg:gap-20'>
                <div>
                    <Reveal>
                        <SectionHeading id='story-title' eyebrow={story.eyebrow} title={story.title} />
                    </Reveal>

                    <div className='mt-10 space-y-6 text-base leading-relaxed text-foreground/80 sm:text-lg'>
                        {story.paragraphs.map((paragraph, index) => (
                            <Reveal key={paragraph} delay={index * 80}>
                                <p className='text-pretty'>
                                    {paragraph}
                                </p>
                            </Reveal>
                        ))}
                    </div>
                </div>

                <Reveal delay={150} className='lg:pt-24'>
                    <aside aria-labelledby='glance-title' className='rounded-2xl border border-border bg-card/80 p-6 sm:p-8'>
                        <div className='flex items-center justify-between gap-4'>
                            <h3 id='glance-title' className='display-heading font-heading text-2xl font-semibold uppercase tracking-tight text-foreground'>
                                {story.glance.title}
                            </h3>

                            <span className='rounded-full border border-race/40 bg-race/10 px-3 py-1 font-mono text-[11px] uppercase tracking-[0.18em] text-race-bright'>
                                LTD
                            </span>
                        </div>

                        <dl className='mt-6 divide-y divide-border'>
                            {story.glance.rows.map((row) => (
                                <div key={row.label} className='grid gap-1 py-4 sm:grid-cols-[9.5rem_1fr] sm:gap-4'>
                                    <dt className='font-mono text-[11px] uppercase leading-5 tracking-[0.14em] text-muted-foreground'>
                                        {row.label}
                                    </dt>
                                    <dd className='text-sm font-medium leading-5 text-foreground'>
                                        {row.value}
                                    </dd>
                                </div>
                            ))}
                        </dl>

                        <a
                            href='#specs'
                            className='group mt-6 inline-flex items-center gap-2 text-sm font-semibold text-race-bright transition-colors hover:text-foreground'
                        >
                            {story.glance.link}
                            <ArrowDown aria-hidden='true' className='size-4 transition-transform group-hover:translate-y-0.5' />
                        </a>
                    </aside>
                </Reveal>
            </div>
        </section>
    )
}

export default StorySection
